import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ArithmeticCheck {
  expr: string;
  stated: number;
  computed: number;
  ok: boolean;
}

export interface TextbookLookup {
  ran: boolean;
  found: boolean;
  match: boolean;
  caSection: string | null;
  greenSection: string | null;
  problem: string | null;
  greenAnswer: string | null;
}

export interface Consensus {
  alternate: string;
  agreeing: number;
}

export interface Verification {
  verdict: 'textbook_verified' | 'verified' | 'uncertain' | 'flagged' | 'no_check';
  confidence: number;
  summary: string;
  consensus: Consensus | null;
  mainAnswer: string;
  strategies: {
    textbook: TextbookLookup;
    arithmetic: { ran: boolean; pass: boolean; checks: ArithmeticCheck[] };
    self_consistency: { ran: boolean; pass: boolean; main: string; alternate: string | null };
    second_opinion: {
      ran: boolean;
      pass: boolean;
      main: string;
      alternate: string | null;
      model: string;
    };
    opus_escalation: {
      ran: boolean;
      pass: boolean;
      main: string;
      alternate: string | null;
      model: string;
    };
  };
}

interface Props {
  verification: Verification | null;  // null = still checking
  finalAnswer?: string;
  chapterTitle?: string;
}

export default function VerificationBadge({ verification, finalAnswer, chapterTitle }: Props) {
  if (!verification) return <PendingCard />;
  if (verification.verdict === 'textbook_verified') {
    return (
      <TextbookVerifiedCard
        verification={verification}
        finalAnswer={finalAnswer}
        chapterTitle={chapterTitle}
      />
    );
  }
  return <StandardCard verification={verification} />;
}

// ============================================================================
// Pending — shown while /api/verify is still running
// ============================================================================

function PendingCard() {
  return (
    <div className="rounded-2xl bg-ink/5 p-4 flex items-center gap-3">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-7 h-7 rounded-full border-[3px] border-ink/15 border-t-sky"
      />
      <div>
        <div className="font-display font-bold text-ink/70">Double-checking…</div>
        <div className="text-xs text-ink/50">
          Comparing against the textbook and three more checkers
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Textbook verified — stamp + receipt (unchanged from Vince's approved design)
// ============================================================================

function TextbookVerifiedCard({
  verification,
  finalAnswer,
  chapterTitle,
}: {
  verification: Verification;
  finalAnswer?: string;
  chapterTitle?: string;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const t = verification.strategies.textbook;

  return (
    <div className="space-y-3">
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 px-5 py-2 border-[3px] border-sky rounded-xl -rotate-[4deg] bg-sky/10 relative">
          <span className="text-xl leading-none">📖</span>
          <span className="font-display font-black text-sky tracking-[0.14em] text-sm uppercase">
            Textbook Verified
          </span>
          <span className="absolute inset-[4px] rounded-md border border-dashed border-sky/40 pointer-events-none" />
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border-2 border-dashed border-sky/30 space-y-2">
        <ReceiptRow
          label="Chapter"
          value={
            t.caSection
              ? chapterTitle
                ? `${t.caSection} · ${chapterTitle}`
                : t.caSection
              : '—'
          }
        />
        <ReceiptRow label="Problem" value={t.problem ?? '—'} />
        <ReceiptRow label="Your answer" value={finalAnswer ?? '—'} />
        <ReceiptRow label="Textbook answer" value={t.greenAnswer ?? '—'} />
        <div className="border-t border-sky/40 pt-2 flex justify-between">
          <span className="text-xs text-ink/60 uppercase tracking-wide font-bold">Match</span>
          <span className="math-display font-bold text-sky">✓ Verified</span>
        </div>
      </div>

      <div className="text-center text-xs text-ink/50">
        Source: Big Ideas Math · 6th grade answer key ·{' '}
        <button
          onClick={() => setShowDetails((v) => !v)}
          className="underline underline-offset-2"
        >
          {showDetails ? 'hide' : 'show'} other checks
        </button>
      </div>

      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <StrategyDetails verification={verification} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReceiptRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1 border-b border-dotted border-ink/10 last:border-0">
      <span className="text-xs text-ink/60 uppercase tracking-wide font-bold shrink-0">
        {label}
      </span>
      <span className="math-display font-bold text-ink text-right">{value}</span>
    </div>
  );
}

// ============================================================================
// Standard card — verified / uncertain / flagged / no_check
// Front-and-center: confidence meter, consensus answer if checkers agree,
// clean strategy list.
// ============================================================================

function StandardCard({ verification }: { verification: Verification }) {
  const [open, setOpen] = useState(false);
  const { verdict, consensus, mainAnswer, strategies } = verification;

  // Determine the display shape based on what actually happened, not a %.
  // - verified:    3 checkers agree with main → celebrate
  // - consensus:   2+ checkers agree on DIFFERENT answer → constructive
  // - ambiguous:   checkers all disagree among themselves → supportive
  // - no_check:    nothing ran
  const claudeAlts = [
    strategies.self_consistency,
    strategies.second_opinion,
    strategies.opus_escalation,
  ]
    .filter((c) => c.ran && c.alternate)
    .map((c) => c.alternate!);
  const uniqueAlts = new Set(claudeAlts.map((a) => a.toLowerCase().replace(/\s/g, '')));
  const isAmbiguous =
    verdict !== 'verified' &&
    !consensus &&
    claudeAlts.length >= 2 &&
    uniqueAlts.size >= 2;

  const shape = consensus
    ? 'consensus'
    : verdict === 'verified'
      ? 'verified'
      : verdict === 'no_check'
        ? 'no_check'
        : isAmbiguous
          ? 'ambiguous'
          : 'mixed';

  const theme = getTheme(shape);

  return (
    <div className={`rounded-2xl ${theme.bg} p-4 space-y-3`}>
      {/* Header — no percentage, just a friendly headline */}
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-12 h-12 rounded-full ${theme.iconBg} flex items-center justify-center`}>
          <span className="text-xl leading-none">{theme.icon}</span>
        </div>
        <div className="flex-1">
          <div className={`font-display font-black text-lg ${theme.text} leading-tight`}>
            {theme.headline}
          </div>
          <div className="text-sm text-ink/70 mt-1">{theme.blurb}</div>
        </div>
      </div>

      {/* Consensus card — the most actionable case. Show her the alternate. */}
      {consensus && (
        <div className="bg-white rounded-xl p-4 border-2 border-sky/30 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-[10px] text-ink/50 uppercase tracking-wide font-bold mb-1">
                You wrote
              </div>
              <div className="math-display font-bold text-ink/60">{mainAnswer}</div>
            </div>
            <div className="text-sky text-2xl pt-2">→</div>
            <div className="flex-1 text-right">
              <div className="text-[10px] text-sky uppercase tracking-wide font-bold mb-1">
                Try this
              </div>
              <div className="math-display font-bold text-sky">{consensus.alternate}</div>
            </div>
          </div>
          <div className="text-xs text-ink/60 pt-2 border-t border-ink/5 text-center">
            {consensus.agreeing} of my 3 checkers got this answer
          </div>
        </div>
      )}

      {/* Ambiguous — no clear consensus, acknowledge it's hard. */}
      {shape === 'ambiguous' && (
        <div className="bg-white rounded-xl p-4 border border-ink/10 space-y-2">
          <div className="font-display font-bold text-sm">💡 What to try next</div>
          <ul className="text-sm text-ink/70 space-y-1 pl-5 list-disc">
            <li>Work through the steps above carefully — take your time</li>
            <li>Ask Dad or your teacher to look at this one with you</li>
            <li>Check the textbook's answer key in the back of the book</li>
          </ul>
        </div>
      )}

      {/* Technical details — hidden by default, only Vince will click */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-xs text-ink/40 underline underline-offset-2 text-center"
      >
        {open ? 'hide' : 'show'} technical details
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <StrategyDetails verification={verification} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type Shape = 'verified' | 'consensus' | 'ambiguous' | 'mixed' | 'no_check';

function getTheme(shape: Shape) {
  switch (shape) {
    case 'verified':
      return {
        bg: 'bg-sky/15',
        iconBg: 'bg-white',
        text: 'text-sky',
        icon: '✓',
        headline: 'All three checkers agree',
        blurb: 'The answer above looks right.',
      };
    case 'consensus':
      return {
        bg: 'bg-sun/25',
        iconBg: 'bg-white',
        text: 'text-ink',
        icon: '💡',
        headline: 'Worth a second look',
        blurb: 'My checkers found a different answer that might be the right one.',
      };
    case 'ambiguous':
      return {
        bg: 'bg-violet/15',
        iconBg: 'bg-white',
        text: 'text-violet',
        icon: '🤔',
        headline: "This one's tricky",
        blurb:
          'Problems like this can have more than one right answer depending on how you read them. Let\'s walk through it together.',
      };
    case 'no_check':
      return {
        bg: 'bg-ink/5',
        iconBg: 'bg-white',
        text: 'text-ink/70',
        icon: '📝',
        headline: "Couldn't auto-check",
        blurb: "Trust your work — or ask your teacher to double-check.",
      };
    case 'mixed':
    default:
      return {
        bg: 'bg-sun/25',
        iconBg: 'bg-white',
        text: 'text-ink',
        icon: '🤔',
        headline: 'Let\'s double-check this one',
        blurb: 'One of my checkers saw it differently. Work through the steps to be sure.',
      };
  }
}

// ============================================================================
// Strategy details — readable breakdown of each checker
// ============================================================================

function StrategyDetails({ verification }: { verification: Verification }) {
  const { strategies, mainAnswer } = verification;
  const t = strategies.textbook;

  return (
    <div className="bg-white rounded-xl p-4 space-y-3 mt-2">
      {/* Textbook */}
      <CheckRow
        name="Big Ideas Math answer key"
        status={
          !t.ran
            ? 'abstain'
            : !t.found
              ? 'abstain'
              : t.match
                ? 'agree'
                : 'disagree'
        }
        yourAnswer={mainAnswer}
        checkerAnswer={t.greenAnswer}
        detail={
          !t.ran
            ? t.caSection
              ? `No entry for chapter ${t.caSection} in our answer key`
              : 'Not a numbered textbook problem'
            : !t.found
              ? `Problem ${t.problem} not published in the key (only odd-numbered problems are)`
              : t.match
                ? 'Matches'
                : `Book says something different — may be an edition mismatch`
        }
      />

      {/* Arithmetic — special treatment because it checks STEPS, not final answer */}
      <ArithmeticRow arith={strategies.arithmetic} />

      {/* 3 Claude checkers */}
      <CheckRow
        name="Second Sonnet check"
        status={
          !strategies.self_consistency.ran
            ? 'abstain'
            : strategies.self_consistency.pass
              ? 'agree'
              : 'disagree'
        }
        yourAnswer={mainAnswer}
        checkerAnswer={strategies.self_consistency.alternate}
      />

      <CheckRow
        name="Haiku second opinion"
        status={
          !strategies.second_opinion.ran
            ? 'abstain'
            : strategies.second_opinion.pass
              ? 'agree'
              : 'disagree'
        }
        yourAnswer={mainAnswer}
        checkerAnswer={strategies.second_opinion.alternate}
      />

      <CheckRow
        name="Opus 4.7 (strongest)"
        status={
          !strategies.opus_escalation.ran
            ? 'abstain'
            : strategies.opus_escalation.pass
              ? 'agree'
              : 'disagree'
        }
        yourAnswer={mainAnswer}
        checkerAnswer={strategies.opus_escalation.alternate}
        detailWhenAbstain="Only runs when other checkers disagree"
      />
    </div>
  );
}

type Status = 'agree' | 'disagree' | 'abstain';

function CheckRow({
  name,
  status,
  yourAnswer,
  checkerAnswer,
  detail,
  detailWhenAbstain,
}: {
  name: string;
  status: Status;
  yourAnswer?: string;
  checkerAnswer?: string | null;
  detail?: string;
  detailWhenAbstain?: string;
}) {
  const chip =
    status === 'agree'
      ? { bg: 'bg-sky/15', text: 'text-sky', icon: '✓', label: 'Agrees' }
      : status === 'disagree'
        ? { bg: 'bg-coral/15', text: 'text-coral', icon: '✗', label: 'Different' }
        : { bg: 'bg-ink/5', text: 'text-ink/50', icon: '·', label: 'Skipped' };

  const showDetail = status === 'abstain' ? detailWhenAbstain ?? detail : detail;

  return (
    <div className="flex items-start gap-3">
      <span
        className={`shrink-0 ${chip.bg} ${chip.text} rounded-lg px-2 py-0.5 font-bold text-xs flex items-center gap-1`}
      >
        <span>{chip.icon}</span>
        <span>{chip.label}</span>
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{name}</div>
        {status === 'disagree' && checkerAnswer && (
          <div className="text-xs text-ink/60 mt-0.5 truncate">
            Got: <span className="math-display font-bold text-coral">{checkerAnswer}</span>
          </div>
        )}
        {showDetail && (
          <div className="text-xs text-ink/50 mt-0.5">{showDetail}</div>
        )}
      </div>
    </div>
  );
}

function ArithmeticRow({
  arith,
}: {
  arith: { ran: boolean; pass: boolean; checks: ArithmeticCheck[] };
}) {
  if (!arith.ran) {
    return (
      <CheckRow
        name="Math in the walkthrough"
        status="abstain"
        detail="No arithmetic claims to check"
      />
    );
  }
  const okCount = arith.checks.filter((c) => c.ok).length;
  const badCount = arith.checks.length - okCount;

  return (
    <div>
      <CheckRow
        name="Math in the walkthrough"
        status={arith.pass ? 'agree' : 'disagree'}
        detail={
          arith.pass
            ? `All ${okCount} computations check out`
            : `${badCount} of ${arith.checks.length} steps had arithmetic errors`
        }
      />
      {!arith.pass && (
        <ul className="mt-2 ml-9 space-y-1">
          {arith.checks
            .filter((c) => !c.ok)
            .map((c, i) => (
              <li key={i} className="text-xs math-display flex gap-2">
                <span className="text-coral">✗</span>
                <span className="flex-1 text-ink/70">
                  {c.expr} = {c.stated}{' '}
                  <span className="text-coral">(actually {c.computed})</span>
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
